import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { SquareEventService } from 'src/app/services/square-event.service';
import { AuthService } from 'src/app/services/auth.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { LoggerService } from '../../../../services/logger.service';

@Component({
  selector: 'app-square-connected-devices',
  templateUrl: './square-connected-devices.component.html',
  styleUrls: ['./square-connected-devices.component.css']
})
export class SquareConnectedDevicesComponent implements OnInit {
  stripeDevices: Array<any> = [];
  selectedDeviceId: string | null = null;
  showProgressSpinner = true;
  errorMessage: string;
  activeDevices: any[] = [];
  inactiveDevices: any[] = [];
  isSquareAuthorized = false;
  constructor(
    private squareEventService: SquareEventService,
    private authService: AuthService,
    private drupalRESTService: DrupalRESTService
  ) { }

  ngOnInit(): void {
    // Only checkSquareAuthorization() runs here: it calls loadStripeDevices()
    // itself once authorization is known. Calling it directly would hit the
    // isSquareAuthorized guard before the check resolves and set a false error.
    this.checkSquareAuthorization();

    // Subscribe to deauthorizeSquare events
    this.squareEventService.deauthorizeSquare$.subscribe(() => {
      this.clearStripeDevices();
      this.isSquareAuthorized = false;
    });
  }

  checkSquareAuthorization() {
    const configFieldName = 'field_square_ref';
    this.drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${configFieldName}`)
      .subscribe(
        (response) => {
          // Check if Square access token exists
          this.isSquareAuthorized = !!response?.['field_square_access_token']?.[0]?.value;

          if (this.isSquareAuthorized) {
            this.selectedDeviceId = response?.['field_square_terminal_device_id']?.[0]?.value || null;
            this.loadStripeDevices();
          } else {
            this.errorMessage = 'You are not connected to Square. Please authorize below.';
            this.showProgressSpinner = false;
          }
        },
        (error) => {
          LoggerService.error('Error checking Square authorization:', error);
          this.errorMessage = 'Error checking Square authorization status.';
          this.showProgressSpinner = false;
        }
      );
  }

  clearStripeDevices() {
    this.stripeDevices = [];
  }

  loadCurrentTerminalDeviceId() {
    const configFieldName = 'field_square_ref';
    this.drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${configFieldName}`)
      .subscribe(
        (response) => {
          // Update to match the actual response structure
          this.selectedDeviceId = response?.['field_square_terminal_device_id']?.[0]?.value || null;

          // After loading the selected device ID, refresh the device list to properly group them
          this.loadStripeDevices();
        },
        (error) => {
          LoggerService.error('Error loading current terminal device ID:', error);
        }
      );
  }

  loadStripeDevices() {
    if (!this.isSquareAuthorized) {
      this.errorMessage = 'You are not connected to Square. Please authorize below.';
      return;
    }

    this.stripeDevices = [];
    this.activeDevices = [];
    this.inactiveDevices = [];
    this.showProgressSpinner = true;
    this.errorMessage = '';

    const studioId = this.authService.getCurrentStudioId();
    if (!studioId) {
      this.errorMessage = 'No studio ID available.';
      this.showProgressSpinner = false;
      return;
    }

    this.drupalRESTService.httpGET(`/api/square/terminal/devices/${studioId}`).subscribe(
      (response: any) => {
        if (response.error) {
          this.errorMessage = response.error;
          this.showProgressSpinner = false;
          return;
        }
        this.stripeDevices = response.devices.map((device: any) => {
          const batteryDetails = device.components.find((c: any) => c.type === 'BATTERY')?.battery_details || {};
          const wifiDetails = device.components.find((c: any) => c.type === 'WIFI')?.wifi_details || {};
          return {
            ...device,
            battery: batteryDetails.visible_percent || 'N/A',
            wifi: wifiDetails.active ? wifiDetails : null,
            status: device.status,
          };
        });

        // Group devices based on selection status
        this.activeDevices = this.stripeDevices.filter(device =>
          device.id === this.selectedDeviceId);
        this.inactiveDevices = this.stripeDevices.filter(device =>
          device.id !== this.selectedDeviceId);

        this.showProgressSpinner = false;
      },
      (error: any) => {
        LoggerService.error('Error loading Square devices:', error);
        if (error.status === 401) {
          this.errorMessage = 'Authentication failed. Please check your Square credentials.';
          this.isSquareAuthorized = false;
        } else {
          this.errorMessage = error.error?.error || 'An error occurred while loading devices.';
        }
        this.showProgressSpinner = false;
      }
    );
  }

  selectDevice(deviceId: string): void {
    this.selectedDeviceId = deviceId;
    this.saveSelectedDevice(deviceId);
  }

  saveSelectedDevice(deviceId: string): void {
    const configFieldName = 'field_square_ref';

    const postData = {
      config_field_name: configFieldName,
      config_data: {
        field_square_terminal_device_id: [{ value: deviceId }]
      }
    };

    this.drupalRESTService
      .httpPOST('/api_rest/v1/saveStudioConfig', postData)
      .subscribe(
        (response) => {
          LoggerService.log('Selected device saved successfully:', response);
          // Regroup devices after successful save
          this.activeDevices = this.stripeDevices.filter(device =>
            device.id === this.selectedDeviceId);
          this.inactiveDevices = this.stripeDevices.filter(device =>
            device.id !== this.selectedDeviceId);
        },
        (error) => {
          LoggerService.error('Error saving selected device:', error);
          this.errorMessage = 'Error saving selected device.';
        }
      );
  }
}
